import { Component, Input, OnChanges, ViewChild } from '@angular/core';
import { PoChartOptions, PoChartSerie, PoChartType, PoModalAction, PoModalComponent, PoTableColumn } from '@po-ui/ng-components';

import { withSinglePointTooltip } from 'src/app/menu/smart-review/core/utils/po-chart-tooltip.util';
import { ReviewStatsBucket } from '../../interfaces/review-stats.interface';
import { integerAxis } from '../../utils/chart-axis.util';
import { formatCount, formatSharePercent } from '../../utils/review-stats-format.util';

/** Quantas chaves entram no gráfico. Acima disso as barras ficam finas demais para comparar. */
const MAX_BARS = 10;

/** Chave que o backend usa para o dado ausente (`area`/`user`). Não é um id de verdade. */
const UNASSIGNED_KEY = '(sem informação)';

/** Cauda agregada pelo backend quando a dimensão passa do teto de fatias. */
const AGGREGATE_TAIL_PATTERN = /^\(outros \d+\)$/;

function isAggregateTailKey(key: string): boolean {
  return AGGREGATE_TAIL_PATTERN.test(key.trim());
}

/** A célula de volume: número, participação e a barra relativa à maior chave. */
interface BreakdownVolume {
  reviews: string;
  share: string;
  width: number;
}

interface BreakdownRow {
  /** Posição por volume; vazio na cauda `(outros N)`, que não é uma chave. */
  rank: string;
  key: string;
  volume: BreakdownVolume;
}

/**
 * Uma dimensão do breakdown de reviews: barras horizontais com as chaves de maior
 * volume e — quando a dimensão tem mais chaves do que cabe no gráfico — "Ver todas",
 * que abre a lista inteira num modal largo com pesquisa, no padrão de "Reviews que pedem
 * ação". A tabela dentro do card espremia o gráfico e a página.
 *
 * A pesquisa é local: o teto de 200 fatias do backend já veio inteiro na resposta, e uma
 * ida ao servidor por tecla não traria nada novo.
 */
@Component({
  selector: 'app-review-stats-breakdown',
  templateUrl: './review-stats-breakdown.component.html',
  styleUrls: ['./review-stats-breakdown.component.css']
})
export class ReviewStatsBreakdownComponent implements OnChanges {
  /** Exposto para o template calcular a altura do gráfico (`Math.max(...)`). */
  readonly Math = Math;

  /** Rótulo da coluna-chave: "Tipo de review", "Origem", "Usuário", "Área". */
  @Input() keyLabel = 'Chave';

  @Input() rows: ReviewStatsBucket[] = [];

  /** Chaves reais da dimensão, antes do teto de fatias do backend. */
  @Input() totalKeys?: number;

  /**
   * Transforma a chave crua num rótulo de exibição (ex.: tipo de review, provedor).
   * Default identidade: a chave já é um valor legível (login, id de área sem
   * catálogo) quando quem chama não passa tradução própria.
   */
  @Input() labelFn: (key: string) => string = key => key;

  readonly barType = PoChartType.Bar;

  categories: string[] = [];
  series: PoChartSerie[] = [];
  chartOptions: PoChartOptions = {};
  tableRows: BreakdownRow[] = [];
  columns: PoTableColumn[] = [];

  /** O que a barra mostra quando ela não mostra tudo — ver `buildChartNote`. */
  chartNote = '';

  @ViewChild(PoModalComponent, { static: true }) modal?: PoModalComponent;

  /** A tabela só é montada com o modal aberto — até 200 linhas que ninguém pediu. */
  expanded = false;
  search = '';
  searchLiterals = { search: 'Pesquisar', clean: 'Limpar pesquisa' };

  /** Resumo acima da grade — o número da dimensão inteira, não o do gráfico. */
  modalSummary = '';
  readonly closeAction: PoModalAction = { label: 'Fechar', action: () => this.closeAll() };

  ngOnChanges(): void {
    // Sem ordenação: a lista já vem por volume, e a posição é o que a coluna # diz.
    this.columns = [
      { property: 'rank', label: '#', width: '64px' },
      { property: 'key', label: this.keyLabel },
      { property: 'volume', label: 'Reviews no período', type: 'columnTemplate', width: '360px' }
    ];
    this.searchLiterals = { search: `Pesquisar ${this.keyLabel.toLowerCase()}`, clean: 'Limpar pesquisa' };

    const buckets = this.rows ?? [];
    const top = buckets.slice(0, MAX_BARS);
    this.categories = top.map(bucket => this.displayKey(bucket.key));
    this.series = top.length
      ? [withSinglePointTooltip(
          { label: 'Reviews', data: top.map(b => b.reviews) },
          top.length === 1 ? formatCount(top[0].reviews) : undefined
        )]
      : [];
    this.chartOptions = integerAxis(top.map(b => b.reviews));
    this.chartNote = this.buildChartNote(top, buckets);

    const totalReviews = buckets.reduce((sum, bucket) => sum + bucket.reviews, 0);
    const largest = buckets.reduce((max, bucket) => Math.max(max, bucket.reviews), 0);
    this.tableRows = buckets.map((bucket, index) => ({
      rank: isAggregateTailKey(bucket.key) ? '' : `${index + 1}º`,
      key: this.displayKey(bucket.key),
      volume: {
        reviews: formatCount(bucket.reviews),
        share: formatSharePercent(bucket.reviews, totalReviews),
        width: largest ? Math.max(2, Math.round((bucket.reviews / largest) * 100)) : 0
      }
    }));
    this.modalSummary = this.buildModalSummary(top, totalReviews);
  }

  get hasData(): boolean {
    return this.series.length > 0;
  }

  /**
   * `(sem informação)` é tratada AQUI, antes de qualquer `labelFn` — inclusive o
   * customizado (área, tipo de review, origem). Sem isso, um `labelFn` passado de
   * fora (que não conhece essa chave especial do backend) mostraria o texto cru na
   * barra e na tabela, e só a dimensão SEM `labelFn` (usuário, que usa o default)
   * ganharia "Não classificado".
   */
  private displayKey(key: string): string {
    if (isAggregateTailKey(key)) { return key; }
    if (key === UNASSIGNED_KEY) { return 'Não classificado'; }
    return this.labelFn(key);
  }

  /**
   * Chaves da dimensão — o número real, não o que coube no gráfico. No fallback
   * (resposta sem `breakdownKeys`) as linhas são a única fonte, e a cauda `(outros N)`
   * não é uma chave — mesmo desconto de `CostsBreakdownSectionComponent.keyCount`.
   */
  get keyCount(): number {
    if (this.totalKeys != null) { return this.totalKeys; }
    const rows = this.rows ?? [];
    const hasTail = rows.length > 0 && isAggregateTailKey(rows[rows.length - 1].key);
    return hasTail ? rows.length - 1 : rows.length;
  }

  get isTruncated(): boolean {
    return this.keyCount > this.categories.length;
  }

  get toggleLabel(): string {
    return `Ver todas (${formatCount(this.keyCount)})`;
  }

  get modalTitle(): string {
    return `${this.keyLabel} — ${formatCount(this.keyCount)} no período`;
  }

  /** As linhas que passam na pesquisa — sem acento e sem distinguir maiúsculas. */
  get visibleRows(): BreakdownRow[] {
    const term = normalize(this.search);
    return term ? this.tableRows.filter(row => normalize(row.key).includes(term)) : this.tableRows;
  }

  get searchNote(): string {
    const n = this.visibleRows.length;
    if (!this.search) { return ''; }
    return n === 1 ? '1 encontrada.' : `${formatCount(n)} encontradas.`;
  }

  openAll(): void {
    this.search = '';
    this.expanded = true;
    this.modal?.open();
  }

  closeAll(): void {
    this.modal?.close();
    this.expanded = false;
  }

  onSearch(value: string): void {
    this.search = (value ?? '').trim();
  }

  /**
   * No modal a lista é inteira, então o texto do gráfico ("as 10 de maior volume")
   * enganaria: o resumo dá o total e deixa as 10 maiores como referência de concentração.
   */
  private buildModalSummary(top: ReviewStatsBucket[], totalReviews: number): string {
    if (!top.length) { return ''; }
    const topReviews = top.reduce((sum, bucket) => sum + bucket.reviews, 0);
    return `${formatCount(totalReviews)} reviews no período · as ${top.length} maiores somam ` +
      `${formatSharePercent(topReviews, totalReviews)}`;
  }

  private buildChartNote(top: ReviewStatsBucket[], all: ReviewStatsBucket[]): string {
    if (!top.length) { return ''; }
    const totalReviews = all.reduce((sum, bucket) => sum + bucket.reviews, 0);
    const topReviews = top.reduce((sum, bucket) => sum + bucket.reviews, 0);
    const share = totalReviews ? `${Math.round((topReviews / totalReviews) * 100)}%` : null;
    return `As ${this.categories.length} de maior volume, de ${formatCount(this.keyCount)}` +
      `${share ? ` — ${share} dos reviews do período` : ''}.`;
  }
}

/** Minúsculo e sem acento: "Gestão" acha "gestao". */
function normalize(value: string): string {
  return (value ?? '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim();
}
